import React from 'react';
import { Building2, FileText, ClipboardList, CheckCircle, Plus } from 'lucide-react';
import { StatCard } from '../components/common';
import type { Organization, SurveyTemplate, Survey, PageView } from '../types';

interface DashboardPageProps {
    organizations: Organization[];
    templates: SurveyTemplate[];
    instances: Survey[];
    onNavigate: (page: PageView) => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
    organizations,
    templates,
    instances,
    onNavigate,
}) => {
    const activeInstances = instances.filter((i) => i.is_active).length;

    const quickActions = [
        {
            icon: <Plus size={20} />,
            label: 'ארגון חדש',
            onClick: () => onNavigate('organizations'),
        },
        {
            icon: <ClipboardList size={20} />,
            label: 'קבוצת סקר חדשה',
            onClick: () => onNavigate('instances'),
        },
    ];

    return (
        <div>
            {/* Stats Grid */}
            <div className="stats-grid">
                <StatCard
                    icon={<Building2 size={24} />}
                    iconVariant="primary"
                    label="ארגונים"
                    value={organizations.length}
                />
                <StatCard
                    icon={<FileText size={24} />}
                    iconVariant="info"
                    label="תבניות סקר"
                    value={templates.length}
                />
                <StatCard
                    icon={<ClipboardList size={24} />}
                    iconVariant="warning"
                    label="סה״כ קבוצות סקר"
                    value={instances.length}
                />
                <StatCard
                    icon={<CheckCircle size={24} />}
                    iconVariant="success"
                    label="סקרים פעילים"
                    value={activeInstances}
                />
            </div>

            {/* Quick Actions */}
            <div className="card" style={{ marginBottom: 'var(--space-8)' }}>
                <div className="card-header">
                    <h3 className="card-title">פעולות מהירות</h3>
                </div>
                <div className="card-body">
                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                        {quickActions.map((action, index) => (
                            <button
                                key={index}
                                className="btn btn-secondary"
                                onClick={action.onClick}
                                style={{ flex: '1', minWidth: '150px', justifyContent: 'center' }}
                            >
                                {action.icon}
                                <span>{action.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="recent-activity-grid">
                {/* Recent Organizations */}
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">ארגונים אחרונים</h3>
                        <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('organizations')}>
                            הצג הכל
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {organizations.length === 0 ? (
                            <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                אין ארגונים עדיין
                            </div>
                        ) : (
                            <table className="data-table">
                                <tbody>
                                    {organizations.slice(0, 5).map((org) => (
                                        <tr key={org.id}>
                                            <td>
                                                <div style={{ fontWeight: 500 }}>{org.name}</div>
                                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
                                                    {org.allowed_email_domain || 'ללא דומיין'}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'left', color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>
                                                {new Date(org.created_at).toLocaleDateString('he-IL')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Recent Instances */}
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">סקרים אחרונים</h3>
                        <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('instances')}>
                            הצג הכל
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {instances.length === 0 ? (
                            <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                אין סקרים עדיין
                            </div>
                        ) : (
                            <table className="data-table">
                                <tbody>
                                    {instances.slice(0, 5).map((instance) => (
                                        <tr key={instance.id}>
                                            <td>
                                                <div style={{ fontWeight: 500 }}>{instance.name}</div>
                                            </td>
                                            <td>
                                                <span className={`badge badge-${instance.is_active ? 'success' : 'neutral'}`}>
                                                    {instance.is_active ? 'פעיל' : 'לא פעיל'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'left', color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>
                                                {new Date(instance.created_at).toLocaleDateString('he-IL')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
